import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, OnInit, OnChanges, OnDestroy, Input, Output, SimpleChanges, Inject, PLATFORM_ID, afterNextRender, NgZone, QueryList, ViewChildren } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Menu, MenuItem } from 'src/app/shared/model/Menu';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { UtilsService } from 'src/app/core/services/utils.service';
import { TableService } from 'src/app/core/services/table.service';
import { DiscountCouponService } from 'src/app/core/services/discount-coupon.service';
import { CategoryView, MenuCategoryCardsComponent } from './menu-category-cards.component';

/** Uma task do pump, para medir o ciclo dela quando a seguinte começar. */
/**
 * Medida de UMA montagem da lista. Sai no `listReady` e vira o
 * `ClientMenuListReady` — a régua de mecanismo do pump.
 */
export interface PumpStats {
	/** `performance.now()` do início da montagem (lote 0). */
	startedAt: number;
	/** Até o lote 0, que é síncrono e pré-paint: o primeiro card na tela. */
	primeiroCardMs: number;
	/** Até a última categoria fechar. */
	prontoMs: number;
	tasks: number;
	piorTaskMs: number;
	/** Soma do que cada task passou de 50 ms. */
	blockingMs: number;
	produtos: number;
	categorias: number;
}

export function emptyPumpStats(): PumpStats {
	return { startedAt: 0, primeiroCardMs: 0, prontoMs: 0, tasks: 0, piorTaskMs: 0, blockingMs: 0, produtos: 0, categorias: 0 };
}

interface TaskRecord {
	start: number;
	/** Fim da task COM as microtasks que ela deixou (`afterTaskMicrotasks`); null até a medida chegar. */
	endAt: number | null;
	produced: number;
	requested: number;
	/** Piso vigente quando a task foi dimensionada. */
	floor: number;
	/** Task dimensionada para um alvo URGENTE (ver `urgentTaskProducts`): ensina o custo por card, mas não o piso. */
	urgent: boolean;
}

/** Um alvo pedido a `materializeThrough`. */
interface Target {
	index: number;
	products: number;
	wanted?: () => boolean;
	urgent?: () => boolean;
	resolve: (reached: boolean) => void;
}

/** Resultado de um avanço do pump — o que mudou e, portanto, quem precisa de change detection. */
interface Advance {
	produced: number;
	started: number;
	/** Categorias que JÁ existiam antes deste avanço e ganharam produtos nele. */
	grown: CategoryView[];
}

@Component({
  selector: 'app-menu-list',
  templateUrl: './menu-list.page.html',
  styleUrls: ['./menu-list.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuListPage implements OnInit, OnChanges, OnDestroy {

	@Input() menu: Menu;
	@Input() info:any;

	/**
	 * Uma vez por montagem da lista, quando a última categoria fecha. Quem emite a
	 * telemetria é a página (ela já tem o serviço); aqui só sai a medida, para o pump
	 * continuar testável sem TestBed e sem telemetria.
	 */
	@Output() listReady = new EventEmitter<PumpStats>();

	// ── Renderização progressiva ─────────────────────────────────────────────────────
	// Montar ~2.000 cards de uma vez bloqueia a main thread por segundos no celular. O
	// 1º lote sai SÍNCRONO (ngOnInit, antes do primeiro paint) e o resto vem em tasks
	// curtas, cada uma depois de um frame.
	//
	// O pump anterior avançava por CATEGORIA INTEIRA a cada setTimeout(48): na loja 8466
	// (planetaconvenienciaanita, 34 categorias / 1.956 produtos visíveis) o lote 0 era
	// CERVEJAS inteira (126 cards) e havia tasks de 403 e 423 produtos. O desenho abaixo:
	//   - custo de CD por task ~constante: cada categoria e cada fatia de 48 cards é uma
	//     fronteira OnPush (menu-category-cards.component.ts), e o pump faz change
	//     detection SÓ no que mudou — categorias e fatias completas nunca mais são visitadas;
	//   - tamanho da task pelo custo REAL do ciclo (ver `runTask`), não só pelo script;
	//   - um frame de verdade entre duas tasks (rAF, e só então a task), para o input e o
	//     scroll entrarem;
	//   - não cresce à toa: com a pessoa rolando só monta o que o scroll vai alcançar, e com
	//     a página escondida espera;
	//   - lote 0 com teto próprio de produtos E de categorias, que não depende da ordem.

	/** Lote 0 (síncrono, pré-paint): 12 cards ≈ 1.260 px na 8466 (~105 px/card) contra
	 *  915-1.072 px de viewport — uma tela cheia. É um TETO exato: nunca absorve o resto
	 *  de uma categoria, então vale para qualquer ordem que o lojista arraste no CRM. */
	private static readonly FIRST_BATCH_PRODUCTS = 12;
	/** Teto de categorias INICIADAS por task, lote 0 incluído. Categoria sem produto
	 *  visível não consome orçamento de produto (é só um `<div>` âncora), então sem este
	 *  teto uma loja com estoque zerado montava o cardápio inteiro antes do 1º paint. */
	private static readonly MAX_CATEGORIES_PER_TASK = 12;
	/**
	 * Tamanho da task: orçamento de TEMPO com um piso de PRODUTOS aprendido pelo frame.
	 *
	 * Um ciclo do pump são duas tasks do browser: a NOSSA (materializar + change detection +
	 * as microtasks que ela deixa — no legado é ali que o Stencil hidrata cada `ion-card`) e a
	 * do FRAME (style, layout, prepaint, layerize, commit). A rodada 2 dimensionava a task só
	 * pelo relógio do script, e errava das duas pontas (trace do Chrome, fixture do gate):
	 *   - a hidratação roda DEPOIS do script: 40 ms de orçamento viravam tasks de 55-65 ms;
	 *   - o frame tem uma parte quase fixa por frame, que cresce com o documento e com a
	 *     lentidão do aparelho (a 10×: layerize ~13 ms, style+layout ~15 ms). Um orçamento em
	 *     ms dá cada vez MENOS cards num aparelho lento — 5 a 8 por task no legado a 10× — e o
	 *     número de frames explode: ~100 frames contra ~45 do pump antigo, +1,6 s só de
	 *     paint/layerize, janela 27% pior.
	 * Agora a task é medida até o fim das microtasks dela (`afterTaskMicrotasks`) e o frame dali até a
	 * task seguinte. O orçamento de `TASK_BUDGET_MS` continua valendo onde o frame é barato;
	 * onde o frame passa de `MAX_FRAME_SHARE` do ciclo, o piso de produtos cresce (ver
	 * `learnFromPreviousTask`). A fração é adimensional e quase não depende do aparelho para
	 * um mesmo tamanho de task (gate, 16 cards: 0,34 a CPU 4× e 0,33 a 10×), então o piso
	 * devolve ao aparelho lento os cards por task que o orçamento em ms tirava dele.
	 *
	 * Varredura com o tamanho FIXO (fixture do gate, janela 1º card → última âncora):
	 *   - CPU 10×:  8 cards 8,3 s · 16 cards 6,4 s · 24 cards 6,2 s (pump antigo ~8,0-8,7 s)
	 *   - CPU  4×:  8 cards 3,2 s · 12 cards 2,9 s · 16 cards 2,5 s · 24 cards 2,3 s, este com
	 *     3× o blocking do de 16 (pump antigo 3,4 s)
	 */
	private static readonly TASK_BUDGET_MS = 40;
	private static readonly MAX_FRAME_SHARE = 0.35;
	/** Abaixo disto o piso desce (a faixa entre os dois evita o vaivém). */
	private static readonly MIN_FRAME_SHARE = 0.28;
	/** Crescimento do piso: ×1,5 na 1ª decisão, ×2 nas seguintes (o layout novo a 10× chega a 96 em ~6 ciclos). */
	private static readonly FLOOR_GROWTH = 1.5;
	private static readonly FLOOR_GROWTH_STREAK = 2;
	private static readonly FLOOR_SHRINK = 1.25;
	/** Teto de produtos por task: governa device rápido (a 1× um card custa ~0,1 ms) e
	 *  segura a visita de CD por task em ≤ 3 fatias. */
	private static readonly MAX_TASK_PRODUCTS = 96;
	/** Piso: abaixo disso o custo fixo da task passa a dominar. */
	private static readonly MIN_TASK_PRODUCTS = 4;
	/** Tamanho da fatia OnPush de cards: metade do teto da task, então uma task toca no
	 *  máximo três fatias, e o cardápio ganha 1 nó a cada 48 cards (+65 na 8466, 0,4%). */
	static readonly CHUNK_SIZE = 48;
	/** Tamanho da 1ª task assíncrona, antes de haver qualquer medição. */
	private static readonly INITIAL_TASK_PRODUCTS = 12;
	/** Ciclos por decisão sobre o piso (a média de 2 dilui a task alheia que caiu num deles). */
	private static readonly FRAME_SHARE_SAMPLES = 2;
	/** Ciclos ignorados no começo: saem junto com o boot do app (frames de 90-300 ms que não são do pump). */
	private static readonly WARMUP_CYCLES = 2;
	/** Rolando: considera a pessoa parada depois deste silêncio de eventos de scroll. */
	static readonly SCROLL_QUIET_MS = 200;
	/** Rolando: só monta se o que falta abaixo da viewport for menos que isto (em telas). */
	static readonly SCROLL_LOOKAHEAD_SCREENS = 3;
	/** Página escondida (aba em segundo plano ou `ion-page-hidden`): intervalo da reconferência. */
	static readonly HIDDEN_POLL_MS = 500;
	/**
	 * Alvo URGENTE (a pill do category-nav: a pessoa pediu aquele destino e está parada
	 * esperando): a task sobe do orçamento de `TASK_BUDGET_MS` para este, e o teto de
	 * `MAX_TASK_PRODUCTS` continua valendo. 40 ms é o orçamento de quem pode estar rolando (o
	 * frame precisa caber junto); 100 ms é o de RESPOSTA — um toque ou um arrasto que chega no
	 * meio da espera ainda é atendido nesse prazo. Cada task paga um frame, então task maior
	 * pousa antes: servir o alvo com as tasks de fundo pousava 35-40% depois do
	 * `renderAllNow()` antigo no legado (rodada 2).
	 *
	 * NUNCA em fração do cardápio (rodada 3: 1/3 dos produtos por task). A task passava a ter o
	 * tamanho da loja, não do aparelho: 545 cards e 363 ms na 8466, 632 cards e 1,4 s num legado
	 * de 1.960 produtos a 4× — o blocking da pill voltava a 86-107% do da base, o touchstart de
	 * quem tentava rolar esperava 933 ms, e o click do toque repetido caía num card.
	 */
	static readonly URGENT_TASK_BUDGET_MS = 100;
	/** Saltos de microtask até a task "terminar" (ver `afterTaskMicrotasks`). */
	private static readonly TASK_MICROTASK_HOPS = 16;

	/** Cancelamento do que está agendado: a próxima task (depois de um frame) ou uma espera. */
	private scheduledTask: (() => void) | null = null;
	/** O agendado é uma ESPERA (página escondida, pessoa rolando), não uma task. */
	private scheduledIsWait = false;
	private msPerProduct: number | null = null;
	/** Piso APRENDIDO de produtos por task (ver `learnFromPreviousTask`). */
	private floorProducts = MenuListPage.MIN_TASK_PRODUCTS;
	private frameShares: number[] = [];
	/** Decisões seguidas de crescer o piso (a partir da 2ª, cresce mais rápido). */
	private floorGrowthStreak = 0;
	/** Ciclos já medidos: os primeiros saem junto com o boot do app e não dizem nada do frame. */
	private cyclesMeasured = 0;
	/** A task anterior, enquanto a próxima é o SEGUIMENTO direto dela (sem espera no meio). */
	private previousTask: TaskRecord | null = null;
	private suspendCount = 0;
	private targets: Target[] = [];
	/** Produtos visíveis por categoria de `menu.general` (a mesma regra de `buildCategoryVM`), calculados uma vez por cardápio. */
	private visibleCounts: number[] | null = null;
	private destroyed = false;
	private channel: MessageChannel | null = null;
	private channelCallback: (() => void) | null = null;
	private scrollEl: HTMLElement | null = null;
	private lastScrollAt = -Infinity;
	private waitingForScroll = false;
	private removeScrollListener: (() => void) | null = null;

	@ViewChildren(MenuCategoryCardsComponent) private categoryCards: QueryList<MenuCategoryCardsComponent>;

	// View-model pré-computado (Caso 16, parte 2): isItemVisible/isItemInStock/
	// showCategory eram chamados para CADA item a CADA ciclo de CD. Como nada
	// muta visibilidade/estoque pós-render (o pipeline do restaurant.service
	// roda antes, e busca/atualização SUBSTITUEM o menu → ngOnChanges), as
	// listas visíveis são calculadas uma única vez por menu recebido.
	public featuredVisible: MenuItem[] = [];
	public combosVisible: MenuItem[] = [];
	public pizzaSizesVisible: MenuItem[] = [];
	public generalView: CategoryView[] = [];

	public pizzaCategoryName;
	public comboCategoryName;
	public featuredItemsCategoryName = '';
	private featuredItemsNameSub: Subscription | null = null;
	public newHomePedirDelivery: boolean;
	// UA não muda durante a sessão: avaliar uma única vez. Guard p/ SSR (sem navigator).
	public readonly isInstagramUA = typeof navigator !== 'undefined' && navigator.userAgent.indexOf('Instagram') > -1;

	private generalProducts: any[];

	// Caches por valor de descrição: os métodos são funções puras de string, mas eram
	// reexecutados (JSON.parse + regexes + sanitize) para cada categoria a cada change
	// detection — e getSafeHtml retornava um SafeHtml novo, re-aplicando [innerHTML].
	private safeHtmlCache = new Map<string, any>();
	private visibleDescriptionCache = new Map<string, boolean>();
	private plainTextCache = new Map<string, string>();

	/**
	 * Medida do pump desta montagem. `blocking_ms` soma o que cada task passou de 50 ms
	 * (a definição de long task): é o que trava o dedo, e não a soma do trabalho — uma
	 * lista de 40 tasks de 45 ms não bloqueia nada e somaria 1,8 s de "trabalho".
	 */
	private stats: PumpStats = emptyPumpStats();
	private listReadyAnnounced = false;

	constructor(
		private translate: TranslateService,
		private utils: UtilsService,
		private tableService: TableService,
		private discountCouponService: DiscountCouponService,
        private sanitizer: DomSanitizer,
        private cdr: ChangeDetectorRef,
        private ngZone: NgZone,
        private host: ElementRef<HTMLElement>,
        @Inject(PLATFORM_ID) private platformId: Object,
	) {
		// Render adiado do menu (Fase 2 perf): o server e a 1ª CD do cliente saem
		// VAZIOS (menos ion-cards hidratados = menos TBT). Após a hidratação, o
		// cliente preenche (rebuild + render progressivo). afterNextRender NÃO roda
		// no server → o render prerenderizado do menu-list fica vazio (o header/LCP,
		// que é outro componente, continua prerenderizado normalmente).
		afterNextRender(() => {
			this.startProgressiveRender();
			this.cdr.markForCheck();
		});
	}

	ngOnInit() {
		this.generalProducts = this.menu.general;
		this.pizzaCategoryName = this.info.pizza_category_name.length > 0 ? this.info.pizza_category_name : 'Pizzas';
		this.comboCategoryName = this.info.combo_category_name.length > 0 ? this.info.combo_category_name : 'Combos';
		// `stream`, não uma leitura só: se o JSON de traduções ainda não chegou, a leitura
		// única devolvia '' e o título "Promoção" ficava em branco a sessão inteira (pego
		// pela sombra do SSR em 25/09/2026). Com a tradução já carregada, emite na hora.
		this.featuredItemsNameSub = this.translate.stream('On Sale').subscribe((nome: string) => {
			this.featuredItemsCategoryName = nome;
			this.cdr.markForCheck();
		});
		this.newHomePedirDelivery = !!this.info.modules.new_home_pedir_delivery;
		// Hero (destaques/combos/pizzas) + o 1º lote PEQUENO da lista geral renderizam no
		// server + 1ª CD do cliente: cards reais no HTML estático ANCORAM o LCP cedo (no
		// FCP). O resto (o grosso dos cards) fica diferido e o afterNextRender preenche no
		// browser após hidratar → TBT baixo. Hidratação casa (server e cliente = mesmo lote).
		this.rebuildHero();
		this.generalView = [];
		this.visibleCounts = null;
		this.startStats();
		this.advance(MenuListPage.FIRST_BATCH_PRODUCTS, MenuListPage.MAX_CATEGORIES_PER_TASK);
		// O lote 0 é síncrono e pré-paint: o "primeiro card" é aqui, não na primeira task.
		this.stats.primeiroCardMs = this.now() - this.stats.startedAt;
		this.announceCouponFromLink();
	}

	/**
	 * Chegada pelo link compartilhável /c/<código> (Cupom v2): confirma pro cliente que o
	 * cupom foi guardado e será aplicado no pagamento. Browser-only: o ngOnInit também
	 * roda no prerender do server, e toast é UI imperativa. O anúncio é consumível UMA
	 * vez, então revisitas ao cardápio (com o cupom ainda pendente) não repetem o toast.
	 * Flag new_coupon OFF: nada é consumido nem mostrado, mesma degradação do restante.
	 * Modo MESA idem: o pedido de mesa sai direto do carrinho/entrega, sem passar pela
	 * tela de pagamento, então "será aplicado no pagamento" seria promessa falsa.
	 */
	private announceCouponFromLink() {
		if (!isPlatformBrowser(this.platformId) || !this.discountCouponService.isActive || this.tableService.currentTable) return;

		const code = this.discountCouponService.consumeLinkAnnouncement();
		if (!code) return;

		this.utils.createToaster(`Cupom ${code} ativado! Monte seu pedido que ele será aplicado no pagamento.`, 5000, 'success');
	}

	ngOnChanges(changes: SimpleChanges) {
		// menu substituído após o init (ex.: resultados de busca no layout legado):
		// renderiza tudo de uma vez — resultados são pequenos e o usuário espera vê-los completos
		if (changes.menu && !changes.menu.firstChange) {
			// os índices pedidos a materializeThrough() eram do cardápio ANTERIOR
			this.settleTargets(false);
			this.rebuildHero();
			this.generalView = [];
			this.visibleCounts = null;
			// Busca do legado troca a lista: é OUTRA montagem, com outra medida.
			this.startStats();
			this.renderAllNow();
			this.stats.primeiroCardMs = this.now() - this.stats.startedAt;
			this.announceListReadyIfDone();
		}
	}

	/** Hero above-the-fold (destaques/combos/pizzas): leve, renderiza no server (ancora o LCP). */
	private rebuildHero() {
		const visivel = (item: any) => this.isItemVisible(item) && this.isItemInStock(item);
		this.featuredVisible = (this.menu?.featuredItems || []).filter(visivel);
		this.combosVisible = (this.menu?.combos || []).filter(visivel);
		this.pizzaSizesVisible = (this.menu?.pizzas?.sizes || []).filter(visivel);
	}

	/** View-model de UMA categoria (filtra produtos + sanitiza descrição). Nasce sem
	 *  nenhum produto materializado: quem materializa, e quantos, é o `advance()`. */
	private buildCategoryVM(category: any): CategoryView {
		const hasDescription = this.hasVisibleDescription(category.description);
		const allProducts = this.visibleProductsOf(category);
		return {
			category,
			allProducts,
			materialized: 0,
			chunks: [],
			revision: 0,
			hasProducts: allProducts.length > 0,
			hasDescription,
			descriptionHtml: hasDescription ? this.getSafeHtml(category.description) : null,
			descriptionText: hasDescription ? this.removeHtmlTags(category.description) : '',
		};
	}

	/**
	 * Avança a materialização em no máximo `productBudget` produtos e `categoryBudget`
	 * categorias iniciadas, continuando exatamente de onde parou (inclusive no meio de uma
	 * categoria). Só muta o view-model; o change detection é de quem chamou.
	 *
	 * INVARIANTE DO PREFIXO — é dele que dependem as âncoras que o `menu.page.ts` procura
	 * por `document.getElementById` (`<nome>-<id>` e o `<h1 [id]="category.id">`): as
	 * categorias são percorridas em ordem e SÓ A ÚLTIMA de `generalView` pode estar
	 * parcial. Logo, se a âncora de uma categoria existe, tudo acima dela está completo e
	 * o topo dela já está na posição final. Categoria com produto visível só é iniciada se
	 * sobra orçamento para ao menos um card no mesmo avanço, então nunca aparece um
	 * cabeçalho sem card embaixo.
	 *
	 * `through`/`throughProducts`: não passa da categoria de índice `through`, nem de
	 * `throughProducts` produtos dentro dela (usado com o pump suspenso, para servir só o
	 * alvo de `materializeThrough`).
	 */
	private advance(productBudget: number, categoryBudget: number, through = Infinity, throughProducts = Infinity): Advance {
		const source = this.menu?.general || [];
		const existing = this.generalView.length;
		const result: Advance = { produced: 0, started: 0, grown: [] };

		while (result.produced < productBudget) {
			const lastIndex = this.generalView.length - 1;
			const last = this.generalView[lastIndex];
			if (last && last.materialized < last.allProducts.length) {
				const room = lastIndex === through
					? Math.min(last.allProducts.length, throughProducts) - last.materialized
					: last.allProducts.length - last.materialized;
				if (room <= 0) break;
				const take = Math.min(productBudget - result.produced, room);
				this.appendProducts(last, take);
				result.produced += take;
				if (lastIndex < existing && result.grown.indexOf(last) < 0) result.grown.push(last);
				continue;
			}
			if (lastIndex >= through) break;
			if (this.generalView.length >= source.length) break;
			if (result.started >= categoryBudget) break;
			this.generalView.push(this.buildCategoryVM(source[this.generalView.length]));
			result.started++;
		}
		return result;
	}

	private visibleProductsOf(category: any): MenuItem[] {
		if (category.unavailable || category.unavailableByTemplate) return [];
		return (category.products || []).filter((item: any) => this.isItemVisible(item) && this.isItemInStock(item));
	}

	/** Produtos visíveis da categoria `index` de `menu.general`, iniciada ou não. */
	private visibleCountOf(index: number): number {
		const view = this.generalView[index];
		if (view) return view.allProducts.length;
		if (!this.visibleCounts) {
			this.visibleCounts = (this.menu?.general || []).map((category: any) => this.visibleProductsOf(category).length);
		}
		return this.visibleCounts[index] ?? 0;
	}

	/** Anexa os próximos `take` produtos da categoria às fatias dela. A fatia que cresce
	 *  ganha um array NOVO (é o que marca o input OnPush); as completas ficam intocadas. */
	private appendProducts(view: CategoryView, take: number) {
		const end = view.materialized + take;
		let from = view.materialized;
		while (from < end) {
			let chunk = view.chunks[view.chunks.length - 1];
			if (!chunk || chunk.products.length >= MenuListPage.CHUNK_SIZE) {
				chunk = { index: view.chunks.length, products: [] };
				view.chunks.push(chunk);
			}
			const n = Math.min(MenuListPage.CHUNK_SIZE - chunk.products.length, end - from);
			chunk.products = chunk.products.concat(view.allProducts.slice(from, from + n));
			from += n;
		}
		view.materialized = end;
		view.revision++;
	}

	trackByCategory(index: number, view: any): any {
		return view?.category?.id ?? index;
	}

	ngOnDestroy() {
		this.destroyed = true;
		this.featuredItemsNameSub?.unsubscribe();
		this.cancelScheduledTask();
		this.settleTargets(false);
		this.removeScrollListener?.();
		this.removeScrollListener = null;
		this.scrollEl = null;
		if (this.channel) {
			this.channel.port1.onmessage = null;
			this.channel.port1.close();
			this.channel = null;
		}
	}

	private startProgressiveRender() {
		// NÃO reseta: o 1º lote já foi materializado no ngOnInit (server + cliente, p/ casar a
		// hidratação e ancorar o LCP). Aqui só agenda o RESTANTE.
		this.watchScroll();
		this.scheduleTask();
	}

	/**
	 * Uma task de montagem. Roda DENTRO da NgZone de propósito: os listeners dos cards são
	 * registrados na zone corrente no momento em que a view é criada, e card criado fora
	 * dela teria clique sem change detection. Com `ngZoneRunCoalescing` (main.ts) isso custa
	 * no máximo um `ApplicationRef.tick()` coalescido por frame, não um por task — e esse
	 * tick encontra a lista limpa, porque o change detection dela já foi feito aqui dentro,
	 * de forma síncrona e só no que mudou.
	 *
	 * `frameAt`: quando o frame que precedeu esta task começou (`yieldToBrowser`). Com ele o
	 * ciclo da task ANTERIOR é medido — ela até o fim das microtasks que deixou, o frame dali
	 * até aqui (`learnFromPreviousTask`). Sem ele (ambiente sem rAF, costura de teste) vale só
	 * o relógio do script.
	 */
	private runTask(frameAt?: number) {
		this.scheduledTask = null;
		this.scheduledIsWait = false;
		if (this.destroyed) return;
		const start = this.now();
		this.learnFromPreviousTask(start, frameAt);
		this.dropUnwantedTargets();
		const limit = this.targets.length ? this.furthestPendingTarget() : null;
		if (this.suspendCount > 0 && !limit) return; // suspenso e sem alvo: quem soltar a última suspensão reagenda
		// alvo pedido (pill, volta) nunca espera: alguém está olhando para ele
		if (!limit && this.waitInstead()) return;

		// com o pump suspenso só o alvo é servido; alvo urgente (pill) com o orçamento de resposta
		const serveOnlyTargets = !!limit && this.suspendCount > 0;
		const urgent = serveOnlyTargets && this.targets.some(t => !!t.urgent && t.urgent());
		const budget = urgent ? this.urgentTaskProducts() : this.nextTaskProducts();
		const result = this.ngZone.run(() => {
			const advanced = serveOnlyTargets
				? this.advance(budget, MenuListPage.MAX_CATEGORIES_PER_TASK, limit.index, limit.products)
				: this.advance(budget, MenuListPage.MAX_CATEGORIES_PER_TASK);
			this.detectChangesFor(advanced);
			return advanced;
		});
		const elapsed = this.now() - start;
		// sem frame para medir a task inteira, o script é a melhor amostra que existe
		if (frameAt === undefined && result.produced >= MenuListPage.MIN_TASK_PRODUCTS) {
			this.learnCardCost(elapsed / result.produced);
		}
		const reached = this.takeReachedTargets();
		this.scheduleTask();
		const record: TaskRecord | null = this.scheduledTask
			? { start, endAt: null, produced: result.produced, requested: budget, floor: this.floorProducts, urgent }
			: null;
		if (record) this.previousTask = record;
		// A medida da task fecha DEPOIS das microtasks (no legado é ali que o Stencil
		// hidrata cada ion-card): medir só o script subestimaria a task em 15-25 ms.
		this.afterTaskMicrotasks(() => {
			if (record) record.endAt = this.now();
			this.countTask(start);
			reached.forEach(target => target.resolve(true));
			this.announceListReadyIfDone();
		});
	}

	/**
	 * Produtos da task que serve um alvo urgente: o orçamento de `URGENT_TASK_BUDGET_MS` pelo
	 * custo por card aprendido, dentro do teto de `MAX_TASK_PRODUCTS`, e nunca menos que a task
	 * de fundo. Sem custo aprendido ainda, a task de fundo (a medida dela dimensiona a próxima).
	 */
	private urgentTaskProducts(): number {
		const background = this.nextTaskProducts();
		if (this.msPerProduct === null) return background;
		const fit = this.msPerProduct > 0 ? Math.floor(MenuListPage.URGENT_TASK_BUDGET_MS / this.msPerProduct) : Infinity;
		return Math.max(background, Math.min(MenuListPage.MAX_TASK_PRODUCTS, fit));
	}

	/**
	 * Amostras do ciclo anterior (ver `runTask`): o custo por card da task inteira, e quanto
	 * do ciclo foi FRAME — que decide o piso de produtos.
	 *
	 * Com o frame acima de `MAX_FRAME_SHARE` do ciclo (média de 2 ciclos do mesmo piso), o
	 * piso cresce a partir do tamanho que a task já tinha; abaixo de `MIN_FRAME_SHARE`, desce.
	 * No legado a fração cai conforme a task cresce, e o piso para onde o frame deixa de
	 * dominar (~15-25 cards, a 4× e a 10×). No layout novo o card é barato de montar e quase
	 * tão caro de renderizar, a fração não cai abaixo do teto e o piso vai a
	 * `MAX_TASK_PRODUCTS`: o que ainda amortiza é a parte fixa.
	 */
	private learnFromPreviousTask(start: number, frameAt?: number) {
		const previous = this.previousTask;
		this.previousTask = null;
		if (!previous || frameAt === undefined || frameAt < previous.start || frameAt > start) return;
		// sem a medida das microtasks, o início do frame (que ainda soma a espera pelo vsync)
		const endAt = previous.endAt !== null && previous.endAt <= frameAt ? previous.endAt : frameAt;
		const taskMs = endAt - previous.start;
		const frameMs = start - endAt;
		// os primeiros ciclos saem junto com o boot do app: nem o custo por card nem o frame deles valem
		if (++this.cyclesMeasured <= MenuListPage.WARMUP_CYCLES) return;
		if (previous.produced >= MenuListPage.MIN_TASK_PRODUCTS) this.learnCardCost(taskMs / previous.produced);
		// a task urgente tem o tamanho do orçamento de resposta, não o do pump de fundo: o frame dela não julga o piso
		if (previous.urgent) return;
		// só a task do tamanho pedido diz algo sobre o tamanho (a última, ou a que parou num alvo, é menor)
		if (previous.produced < previous.requested || taskMs + frameMs <= 0) return;
		// só conta a task dimensionada sob o piso ATUAL: a de antes da última decisão já foi julgada
		if (previous.floor !== this.floorProducts) return;
		this.frameShares.push(frameMs / (taskMs + frameMs));
		if (this.frameShares.length < MenuListPage.FRAME_SHARE_SAMPLES) return;
		const share = this.frameShares.reduce((a, b) => a + b, 0) / this.frameShares.length;
		this.frameShares = [];
		if (share > MenuListPage.MAX_FRAME_SHARE) {
			const factor = this.floorGrowthStreak > 0 ? MenuListPage.FLOOR_GROWTH_STREAK : MenuListPage.FLOOR_GROWTH;
			const grown = Math.ceil(Math.max(this.floorProducts, previous.produced) * factor);
			this.floorProducts = Math.min(MenuListPage.MAX_TASK_PRODUCTS, grown);
			this.floorGrowthStreak++;
		} else {
			this.floorGrowthStreak = 0;
			if (share < MenuListPage.MIN_FRAME_SHARE) {
				this.floorProducts = Math.max(MenuListPage.MIN_TASK_PRODUCTS, Math.floor(this.floorProducts / MenuListPage.FLOOR_SHRINK));
			}
		}
	}

	/**
	 * Roda `callback` quando a task terminou DE VERDADE: depois das microtasks que ela deixou.
	 * A hidratação Stencil do legado roda em cadeias de microtask (`queueMicrotask` por
	 * componente, e de novo para os filhos), então uma microtask só chegaria cedo demais: cada
	 * salto volta para o FIM da fila, e 16 saltos passam das cadeias que existem aqui. Serve a
	 * duas coisas:
	 *   - o fim da task para o custo por card (`TaskRecord.endAt`). Sem ele, a única régua é o
	 *     início do frame, que soma a espera pelo vsync e inflava o custo das tasks pequenas;
	 *   - a resolução de `materializeThrough`. Quem espera o alvo MEDE em seguida, e no legado
	 *     um `ion-card` ainda não hidratado não tem a margem do `:host` (o estilo entra com o
	 *     shadow root): a pill mirava curto pela hidratação que faltava.
	 */
	private afterTaskMicrotasks(callback: () => void) {
		if (typeof queueMicrotask !== 'function') {
			callback();
			return;
		}
		let hops = MenuListPage.TASK_MICROTASK_HOPS;
		const hop = () => {
			if (--hops > 0) queueMicrotask(hop);
			else callback();
		};
		queueMicrotask(hop);
	}

	private learnCardCost(sample: number) {
		this.msPerProduct = this.msPerProduct === null ? sample : this.msPerProduct * 0.7 + sample * 0.3;
	}

	/** Produtos da próxima task: o orçamento de tempo dividido pelo custo por card, nunca abaixo do piso aprendido. */
	private nextTaskProducts(): number {
		if (this.msPerProduct === null) return MenuListPage.INITIAL_TASK_PRODUCTS;
		const fit = this.msPerProduct > 0 ? Math.floor(MenuListPage.TASK_BUDGET_MS / this.msPerProduct) : Infinity;
		return Math.max(this.floorProducts, Math.min(MenuListPage.MAX_TASK_PRODUCTS, fit));
	}

	/**
	 * Motivos para NÃO montar agora (sem alvo pedido). Agenda a reconferência e devolve true.
	 *   - Página escondida (aba em segundo plano, ou a MenuPage do legado sob o
	 *     `ion-router-outlet`, `display:none`): ninguém vê o que se monta, e cada task é CPU
	 *     de fundo. Reconfere a cada `HIDDEN_POLL_MS`.
	 *   - Pessoa rolando com bastante conteúdo abaixo da viewport: cada task é um frame
	 *     longo no meio do scroll. Espera o silêncio, ou o scroll chegar perto do fim do que
	 *     existe (o listener de scroll reagenda na hora).
	 */
	private waitInstead(): boolean {
		let waitMs = 0;
		const sinceScroll = this.now() - this.lastScrollAt;
		if (this.isHidden()) {
			waitMs = MenuListPage.HIDDEN_POLL_MS;
		} else if (sinceScroll < MenuListPage.SCROLL_QUIET_MS && !this.scrollNeedsContent()) {
			waitMs = MenuListPage.SCROLL_QUIET_MS - sinceScroll;
			this.waitingForScroll = true;
		}
		if (!waitMs) return false;
		this.scheduledIsWait = true;
		this.scheduledTask = this.delay(() => {
			this.scheduledTask = null;
			this.scheduledIsWait = false;
			this.waitingForScroll = false;
			this.scheduleTask();
		}, waitMs);
		return true;
	}

	private isHidden(): boolean {
		if (typeof document !== 'undefined' && document.hidden) return true;
		// sem caixa de layout (`display:none` num ancestral) o container de scroll mede 0
		const el = this.scrollEl;
		return !!el && el.isConnected && el.clientHeight === 0;
	}

	/** O que já existe abaixo da viewport acaba em menos de `SCROLL_LOOKAHEAD_SCREENS` telas? */
	private scrollNeedsContent(): boolean {
		const el = this.scrollEl;
		if (!el) return true;
		const below = el.scrollHeight - el.scrollTop - el.clientHeight;
		return below < MenuListPage.SCROLL_LOOKAHEAD_SCREENS * el.clientHeight;
	}

	/** Liga o listener de scroll do container (o `div.menu-scroll-container` que hospeda a lista), fora da zone. */
	private watchScroll() {
		if (this.removeScrollListener || !isPlatformBrowser(this.platformId)) return;
		this.removeScrollListener = () => {}; // uma vez só, mesmo antes de o container chegar
		this.findScrollElement().then(el => {
			if (!el || this.destroyed) return;
			this.scrollEl = el;
			const onScroll = () => {
				this.lastScrollAt = this.now();
				// esperando o scroll parar, mas ele chegou perto do fim do que existe: monta já
				if (this.waitingForScroll && this.scrollNeedsContent()) {
					this.waitingForScroll = false;
					this.cancelScheduledTask();
					this.scheduleTask();
				}
			};
			this.ngZone.runOutsideAngular(() => el.addEventListener('scroll', onScroll, { passive: true }));
			this.removeScrollListener = () => el.removeEventListener('scroll', onScroll);
		}).catch(() => {});
	}

	/**
	 * Container de scroll da lista (costura de teste).
	 *
	 * Sem Ionic não há `ion-content` com `getScrollElement()`: o scroll mora no próprio
	 * `<div #scrollContainer class="menu-scroll-container">` da MenuPage. Procurar o
	 * `ion-content` aqui devolveria `null` em silêncio — o pump nunca esperaria o scroll
	 * parar (`scrollNeedsContent()` sem container diz sempre "precisa") nem notaria a página
	 * escondida, e as tasks cairiam no meio do scroll da pessoa.
	 */
	private findScrollElement(): Promise<HTMLElement | null> {
		const container = this.host?.nativeElement?.closest?.('.menu-scroll-container') as HTMLElement | null;
		return Promise.resolve(container ?? null);
	}

	/**
	 * Change detection só no que o avanço mudou. Categoria iniciada muda a lista de
	 * categorias, então o pai precisa de refresh (O(categorias), no máximo uma vez por
	 * categoria da loja); ele alcança as categorias que cresceram pelo input `revision`.
	 * Sem categoria nova, basta a categoria que cresceu — as outras nem são visitadas.
	 */
	private detectChangesFor(result: Advance) {
		if (result.started === 0) {
			const cards = this.categoryCards;
			const refreshed = result.grown.every(view => {
				const comp = cards?.find(c => c.view === view);
				if (comp) comp.refresh();
				return !!comp;
			});
			if (refreshed) return;
		}
		this.cdr.detectChanges();
	}

	private scheduleTask() {
		if (this.destroyed || !isPlatformBrowser(this.platformId)) return;
		// um alvo pedido não fica atrás de uma espera
		if (this.scheduledTask && this.scheduledIsWait && this.targets.length) this.cancelScheduledTask();
		if (this.scheduledTask || this.isFullyRendered) return;
		this.dropUnwantedTargets();
		if (this.suspendCount > 0 && !this.furthestPendingTarget()) return;
		this.scheduledTask = this.yieldToBrowser(frameAt => this.runTask(frameAt));
	}

	private cancelScheduledTask() {
		this.previousTask = null;
		this.waitingForScroll = false;
		this.scheduledIsWait = false;
		if (this.scheduledTask) {
			const cancel = this.scheduledTask;
			this.scheduledTask = null;
			cancel();
		}
	}

	/**
	 * Devolve a main thread ao browser ATÉ O PRÓXIMO FRAME e roda `callback` na task
	 * seguinte a ele: rAF (que roda no começo do frame, depois do input) posta uma mensagem
	 * por MessageChannel, que só é atendida quando o frame terminou de renderizar. É o que
	 * garante um frame — e o input do scroll — entre duas tasks: a rodada 2 emendava tasks
	 * de ~40 ms e o scroll durante o pump ganhava mais frames acima de 50 ms. O rAF também
	 * dá o `frameAt` que mede o ciclo (ver `runTask`) e para sozinho em aba escondida.
	 * MessageChannel e não setTimeout(0): cadeias de setTimeout aninhado sofrem o clamp de
	 * 4 ms. Tudo fora da NgZone; `runTask` entra nela explicitamente. Devolve o cancelamento.
	 */
	private yieldToBrowser(callback: (frameAt?: number) => void): () => void {
		if (typeof MessageChannel === 'undefined' || typeof requestAnimationFrame === 'undefined') {
			const handle = setTimeout(() => callback(), 0);
			return () => clearTimeout(handle);
		}
		if (!this.channel) {
			this.ngZone.runOutsideAngular(() => {
				this.channel = new MessageChannel();
				this.channel.port1.onmessage = () => {
					const fn = this.channelCallback;
					this.channelCallback = null;
					if (fn) fn();
				};
			});
		}
		let raf = 0;
		let posted: (() => void) | null = null;
		this.ngZone.runOutsideAngular(() => {
			raf = requestAnimationFrame(() => {
				raf = 0;
				const frameAt = this.now();
				posted = () => callback(frameAt);
				this.channelCallback = posted;
				this.channel?.port2.postMessage(null);
			});
		});
		return () => {
			if (raf) cancelAnimationFrame(raf);
			if (posted && this.channelCallback === posted) this.channelCallback = null;
		};
	}

	/** Espera `ms` fora da zone (costura de teste). Devolve o cancelamento. */
	private delay(callback: () => void, ms: number): () => void {
		let handle: ReturnType<typeof setTimeout>;
		this.ngZone.runOutsideAngular(() => { handle = setTimeout(callback, ms); });
		return () => clearTimeout(handle);
	}

	/** Relógio das tasks (costura de teste: o spec troca por um relógio determinístico). */
	private now(): number {
		return performance.now();
	}

	// trackBy (Caso 16): sem ele, trocar a referência do menu (ex.: busca no
	// layout legado) recriava o DOM de TODOS os cards em vez de reaproveitar.
	trackById(index: number, item: any): any {
		return item?.id ?? index;
	}

	// ── API pública do render progressivo ─────────────────────────────────────────────

	/**
	 * Materializa TUDO agora, numa task só, e marca para o próximo change detection.
	 * Semântica e assinatura de sempre — inclusive ignorando suspensões. É a task cara que
	 * o pump existe para evitar; para chegar a uma categoria, prefira `materializeThrough`.
	 */
	renderAllNow() {
		this.cancelScheduledTask();
		this.advance(Infinity, Infinity);
		this.cdr.markForCheck();
		this.settleTargets(true);
	}

	/**
	 * Materializa, em tasks limitadas pelo mesmo teto do pump, até a categoria de índice
	 * `index` (em `menu.general`) estar COMPLETA: a âncora dela existe, o topo está na
	 * posição final e a altura dela também. Resolve `true` quando isso vale (na hora, se
	 * já valia); `false` se o índice não existe, se o cardápio foi trocado (os índices eram
	 * do anterior) ou se a lista foi destruída antes.
	 *
	 * `products` (opcional, ≥ 1) encurta o alvo para "os primeiros `products` produtos
	 * visíveis da categoria" (ou todos, se ela tiver menos). Serve a quem só precisa de
	 * ALGUM conteúdo abaixo de um ponto — o scroll que precisa caber — e não deve esperar
	 * uma categoria de 400 cards inteira por isso. Pelo invariante do prefixo, a âncora e o
	 * topo da categoria já estão no lugar final com qualquer `products`.
	 *
	 * `wanted` (opcional) é o abort: consultado antes de cada task, e quando devolve `false` o
	 * alvo sai da fila e a promise resolve `false`. Sem isso, quem desistiu (um gesto
	 * cancelou a volta de um item, outra pill substituiu a primeira) deixava o alvo
	 * registrado, e o pump seguia montando até ele mesmo suspenso — por exemplo, no meio da
	 * animação da pill seguinte. Precisa ser barato e sem efeito colateral.
	 *
	 * `urgent` (opcional, mesmas restrições de `wanted`): enquanto devolve `true` e o pump está
	 * suspenso, o alvo é servido em tasks grandes (`urgentTaskProducts`) — é a pill, com a
	 * pessoa olhando para a tela parada. Quando vira `false` (ela começou a rolar), o alvo
	 * continua na fila, mas nas tasks do pump de fundo, que não travam o scroll dela.
	 *
	 * Serve o alvo mesmo com o pump suspenso — nesse caso para exatamente nele. Resolve
	 * depois do change detection da task que o alcançou E das microtasks que ela deixou
	 * (a hidratação do legado), então o DOM já está lá, com a altura final.
	 */
	materializeThrough(index: number, products = Infinity, wanted?: () => boolean, urgent?: () => boolean): Promise<boolean> {
		const source = this.menu?.general || [];
		if (!(index >= 0 && index < source.length) || this.destroyed) return Promise.resolve(false);
		products = Math.max(1, products || 0);
		if (this.isReached(index, products)) return Promise.resolve(true);
		if (!isPlatformBrowser(this.platformId)) return Promise.resolve(false);
		return new Promise<boolean>(resolve => {
			this.targets.push({ index, products, wanted, urgent, resolve });
			this.scheduleTask();
		});
	}

	/**
	 * O ponto `extraProducts` produtos visíveis depois do INÍCIO da categoria `index`, que
	 * pode cair numa categoria seguinte: `{ index, products }` no formato de
	 * `materializeThrough`. Quem precisa de uma tela de conteúdo abaixo de uma âncora pede
	 * tudo de uma vez, em vez de chegar na âncora e crescer depois (uma espera a mais).
	 */
	positionAfter(index: number, extraProducts: number): { index: number; products: number } {
		const last = (this.menu?.general?.length ?? 0) - 1;
		let remaining = Math.max(1, Math.ceil(extraProducts));
		for (let i = Math.max(0, index); i <= last; i++) {
			const count = this.visibleCountOf(i);
			if (remaining <= count) return { index: i, products: remaining };
			remaining -= count;
		}
		return { index: Math.max(index, last), products: Infinity };
	}

	/** Elemento que abre a lista (o 1º filho do host, sempre um bloco): referência de posição para quem está acima dela. */
	get listElement(): HTMLElement | null {
		const host = this.host?.nativeElement;
		return (host?.firstElementChild as HTMLElement) ?? host ?? null;
	}

	/** Índice da categoria de id `categoryId` em `menu.general` (-1 se não existe). */
	categoryIndexOf(categoryId: string | number): number {
		return (this.menu?.general || []).findIndex((c: any) => String(c?.id) === String(categoryId));
	}

	/**
	 * Suspende o avanço do pump em segundo plano (ex.: durante um scroll animado, para não
	 * montar cards na main thread no meio da animação). CONTADO: cada chamada devolve a sua
	 * própria função de liberação, idempotente, e o pump só volta quando TODAS as suspensões
	 * vivas forem liberadas — dois donos não se desarmam. `materializeThrough` continua
	 * sendo atendido durante a suspensão; `renderAllNow` também.
	 */
	suspendProgressiveRender(): () => void {
		this.suspendCount++;
		let released = false;
		return () => {
			if (released) return;
			released = true;
			this.suspendCount--;
			if (this.suspendCount === 0) this.scheduleTask();
		};
	}

	/** Zera a medida: uma montagem, uma medida (o ngOnChanges da busca começa outra). */
	private startStats() {
		this.stats = emptyPumpStats();
		this.stats.startedAt = this.now();
		this.listReadyAnnounced = false;
	}

	/** Uma task do pump terminou (microtasks incluídas). */
	private countTask(start: number) {
		const ms = this.now() - start;
		this.stats.tasks++;
		if (ms > this.stats.piorTaskMs) this.stats.piorTaskMs = ms;
		// long task = 50 ms; só o excedente é o que a pessoa sente travar
		if (ms > 50) this.stats.blockingMs += ms - 50;
	}

	/**
	 * A lista fechou. Anuncia UMA vez por montagem: o pump agenda task enquanto houver
	 * alvo, e sem a guarda a última categoria emitiria um evento por task ociosa.
	 */
	private announceListReadyIfDone() {
		if (this.listReadyAnnounced || this.destroyed || !this.isFullyRendered) return;
		this.listReadyAnnounced = true;
		this.stats.prontoMs = this.now() - this.stats.startedAt;
		this.stats.produtos = this.renderedProductCount;
		this.stats.categorias = this.generalView.length;
		this.ngZone.run(() => this.listReady.emit({ ...this.stats }));
	}

	/** Medida da montagem atual (costura de teste; a página lê pelo `listReady`). */
	get pumpStats(): PumpStats {
		return { ...this.stats };
	}

	get isProgressiveRenderSuspended(): boolean {
		return this.suspendCount > 0;
	}

	/** Produtos da lista geral já materializados. */
	get renderedProductCount(): number {
		return this.generalView.reduce((n, view) => n + view.materialized, 0);
	}

	get isFullyRendered(): boolean {
		// todas as categorias iniciadas E a última (a única que pode estar parcial) completa
		if (this.generalView.length < (this.menu?.general?.length || 0)) return false;
		const last = this.generalView[this.generalView.length - 1];
		return !last || last.materialized >= last.allProducts.length;
	}

	private isReached(index: number, products: number): boolean {
		const view = this.generalView[index];
		return !!view && view.materialized >= Math.min(products, view.allProducts.length);
	}

	/** Tira da fila (resolvendo `false`) os alvos cujo dono desistiu. */
	private dropUnwantedTargets() {
		if (!this.targets.some(t => t.wanted && !t.wanted())) return;
		const alive = [];
		for (const target of this.targets) {
			if (target.wanted && !target.wanted()) target.resolve(false);
			else alive.push(target);
		}
		this.targets = alive;
	}

	/** O alvo pendente mais adiante (categoria, e produtos dentro dela); `null` sem alvo. */
	private furthestPendingTarget(): { index: number; products: number } | null {
		let furthest: { index: number; products: number } | null = null;
		for (const t of this.targets) {
			if (!furthest || t.index > furthest.index || (t.index === furthest.index && t.products > furthest.products)) {
				furthest = { index: t.index, products: t.products };
			}
		}
		return furthest;
	}

	/** Tira da fila os alvos já completos (quem resolve é quem chama). */
	private takeReachedTargets(): Target[] {
		if (!this.targets.length) return [];
		const reached: Target[] = [];
		const pending: Target[] = [];
		for (const target of this.targets) (this.isReached(target.index, target.products) ? reached : pending).push(target);
		this.targets = pending;
		return reached;
	}

	/** reached=true: resolve os alvos já completos. reached=false: resolve todos com false. */
	private settleTargets(reached: boolean) {
		if (!this.targets.length) return;
		const pending = [];
		for (const target of this.targets) {
			if (!reached) target.resolve(false);
			else if (this.isReached(target.index, target.products)) target.resolve(true);
			else pending.push(target);
		}
		this.targets = pending;
	}

	get showStockPaused(): boolean {
		if (this.info?.modules?.ingredient_inventory) {
			return this.info?.supply_stock_config?.display_paused_items_as_sold_out ?? false;
		}
		return this.info?.stock_configuration?.display_paused_items_as_sold_out ?? false;
	}

	isItemVisible(item: MenuItem): boolean {
		return !item.unavailable && !item.unavailableByTemplate && item.totalCalcType !== 'WEIGHT';
	}

	isItemInStock(item: MenuItem): boolean {
		return item.hasStock !== false || this.showStockPaused;
	}

	showCategory(items: MenuItem[]): boolean {
		if (!items) return false;
		return items.some(item => this.isItemVisible(item) && this.isItemInStock(item));
	}

	logScrolling(evento) {
		console.log(evento);
	}

	convertToSafeHtml(rawHtml: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(rawHtml);
    }

	isInstagram() {
		var ua = navigator.userAgent;
		return (ua.indexOf('Instagram') > -1) ? true : false;
	}

	removeHtmlTags(str: string) {
		const cached = this.plainTextCache.get(str);
		if (cached !== undefined) return cached;
		const result = this.utils.removeTags(str);
		this.plainTextCache.set(str, result);
		return result;
	}

	hasVisibleDescription(desc: string): boolean {
		if (!desc) return false;
		const cached = this.visibleDescriptionCache.get(desc);
		if (cached !== undefined) return cached;
		const result = !!this.removeHtmlTags(desc)?.trim();
		this.visibleDescriptionCache.set(desc, result);
		return result;
	}

    getSafeHtml(value: any) {
		const cached = this.safeHtmlCache.get(value);
		if (cached !== undefined) return cached;
		let result;
		try {
			const parsedValue = JSON.parse(value);
			const sanitizedValue = this.utils.sanitizeAndFormatText(parsedValue);
			result = this.convertToSafeHtml(sanitizedValue);
		} catch (error) {
			const sanitizedValue = this.utils.sanitizeAndFormatText(value);
			result = this.removeHtmlTags(sanitizedValue);
		}
		this.safeHtmlCache.set(value, result);
		return result;
    }
}
